/**
 * WebRTC Optimization: Управление подписками на видеопотоки на основе видимости
 *
 * ОТВЕТСТВЕННОСТЬ: Отслеживание видимости + subscribe/unsubscribe
 * Качество устанавливается через useVideoQualityByParticipantCount
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Participant, RemoteTrackPublication, Room, Track } from 'livekit-client';
import { createLogger } from '@web-chat-core/toolkit';
import { captureParticipantSnapshot } from 'hooks/useVideoSnapshot';
import { INTERSECTION_OBSERVER_CONFIG, LARGE_ROOM_PARTICIPANTS_THRESHOLD } from '../consts';

const logger = createLogger({ prefix: 'WebRTC Optimization' });

interface UseParticipantSubscriptionsOptions {
    room?: Room;
    participants: Participant[];
    maxVisible?: number;
    isSidebarMode?: boolean;
    speakerParticipantId?: string;
    threshold?: number;
}

export const useParticipantSubscriptions = ({
    room,
    participants,
    maxVisible = LARGE_ROOM_PARTICIPANTS_THRESHOLD,
    isSidebarMode = false,
    speakerParticipantId,
    threshold = 0.6,
}: UseParticipantSubscriptionsOptions) => {
    const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
    const observerRef = useRef<IntersectionObserver | null>(null);
    const elementsRef = useRef<Map<string, Element>>(new Map());
    const previousSubscriptionsRef = useRef<Set<string>>(new Set());

    // IntersectionObserver для отслеживания видимости
    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                setVisibleIds((prev) => {
                    const newVisible = new Set(prev);

                    for (const entry of entries) {
                        const id = entry.target.getAttribute('data-participant-id');
                        if (!id) continue;

                        if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
                            newVisible.add(id);
                        } else {
                            newVisible.delete(id);
                        }
                    }

                    return newVisible;
                });
            },
            {
                root: INTERSECTION_OBSERVER_CONFIG.root,
                rootMargin: INTERSECTION_OBSERVER_CONFIG.rootMargin,
                threshold: threshold || INTERSECTION_OBSERVER_CONFIG.defaultThreshold,
            },
        );

        elementsRef.current.forEach((element) => {
            observerRef.current?.observe(element);
        });

        return () => {
            observerRef.current?.disconnect();
        };
    }, [threshold]);

    // Очистка элементов участников, которых больше нет
    useEffect(() => {
        const currentIds = new Set(participants.map((p) => p.identity));

        elementsRef.current.forEach((element, id) => {
            if (!currentIds.has(id) && observerRef.current) {
                observerRef.current.unobserve(element);
                elementsRef.current.delete(id);
            }
        });
    }, [participants]);

    // Определяем список участников для подписки (мемоизировано для оптимизации)
    const activeVisible = useMemo(() => {
        const activeVisible = new Set<string>();
        const remoteParticipants = participants.filter((p) => !p.isLocal);
        const localId = room?.localParticipant?.identity;
        // Создаем Set для быстрой проверки существования участников (O(1) проверка)
        const remoteParticipantIds = new Set(remoteParticipants.map((p) => p.identity));

        if (visibleIds.size === 0) {
            // Начальный рендер — только удалённые, не больше maxVisible
            remoteParticipants.slice(0, maxVisible).forEach((p) => activeVisible.add(p.identity));
        } else if (isSidebarMode) {
            // Сайдбар - все видимые, кроме локального, но только если участник еще существует
            visibleIds.forEach((id) => {
                if (id !== localId && remoteParticipantIds.has(id)) {
                    activeVisible.add(id);
                }
            });
        } else {
            // Grid - ограничиваем maxVisible, исключая локального
            // Фильтруем только тех, кто видим И еще существует в participants
            remoteParticipants
                .filter((p) => visibleIds.has(p.identity))
                .slice(0, maxVisible)
                .forEach((p) => activeVisible.add(p.identity));
        }

        // Добавляем спикера в сайдбаре (если он не локальный и существует)
        if (
            isSidebarMode &&
            speakerParticipantId &&
            speakerParticipantId !== localId &&
            remoteParticipantIds.has(speakerParticipantId)
        ) {
            activeVisible.add(speakerParticipantId);
        }

        return activeVisible;
    }, [participants, visibleIds, maxVisible, isSidebarMode, speakerParticipantId, room?.localParticipant?.identity]);

    // Управление подписками
    useEffect(() => {
        if (!room) return;

        const totalParticipants = participants.length;

        // Grid режим (≤LARGE_ROOM_PARTICIPANTS_THRESHOLD участников) - подписываемся на всех
        if (!isSidebarMode && totalParticipants <= LARGE_ROOM_PARTICIPANTS_THRESHOLD) {
            participants.forEach((participant) => {
                if (participant.isLocal) return;
                participant.videoTrackPublications.forEach((publication) => {
                    if (
                        publication.source === Track.Source.Camera &&
                        publication instanceof RemoteTrackPublication &&
                        !publication.isSubscribed
                    ) {
                        publication.setSubscribed(true);
                    }
                });
            });
            return;
        }

        // Проверяем, изменились ли подписки (оптимизированное сравнение множеств)
        const previousSubs = previousSubscriptionsRef.current;
        const hasChanged = (() => {
            if (previousSubs.size !== activeVisible.size) return true;

            // Проверяем, есть ли новые элементы или удаленные
            for (const id of activeVisible) {
                if (!previousSubs.has(id)) return true;
            }
            return false;
        })();

        if (!hasChanged) {
            return;
        }

        previousSubscriptionsRef.current = new Set(activeVisible);

        // Применяем подписки
        let subscribedCount = 0;
        let unsubscribedCount = 0;

        participants.forEach((participant) => {
            if (participant.isLocal) return;

            const shouldSubscribe = activeVisible.has(participant.identity);

            participant.videoTrackPublications.forEach((publication) => {
                if (publication.source === Track.Source.Camera && publication instanceof RemoteTrackPublication) {
                    const isSubscribed = publication.isSubscribed;
                    if (shouldSubscribe && !isSubscribed) {
                        publication.setSubscribed(true);
                        subscribedCount++;
                    } else if (!shouldSubscribe && isSubscribed) {
                        captureParticipantSnapshot(participant.identity);
                        publication.setSubscribed(false);
                        unsubscribedCount++;
                    }
                }
            });
        });

        // Логируем только если были изменения
        if (subscribedCount > 0 || unsubscribedCount > 0) {
            // Используем activeVisible.size вместо пересчета - это уже количество активных подписок
            logger.debug(
                `Подписки обновлены: +${subscribedCount} подписано, -${unsubscribedCount} отписано. ` +
                    `Всего подписано на видео: ${activeVisible.size}/${totalParticipants} участников. ` +
                    `Видимых участников: ${visibleIds.size}, Активных подписок: ${activeVisible.size}`,
            );
        }
    }, [room, participants, visibleIds, maxVisible, isSidebarMode, speakerParticipantId]);

    // Callback ref для регистрации элементов
    const setElementRef = useCallback(
        (id: string) => (element: HTMLDivElement | null) => {
            const oldElement = elementsRef.current.get(id);

            if (oldElement && oldElement !== element && observerRef.current) {
                observerRef.current.unobserve(oldElement);
                elementsRef.current.delete(id);
            }

            if (element) {
                elementsRef.current.set(id, element);
                observerRef.current?.observe(element);
            }
        },
        [],
    );

    return { setElementRef };
};
